import React, { useState } from 'react';
import { FiCheck, FiArrowUpRight } from 'react-icons/fi';
import ManufacturingImage from '../../../../Assets/Odoo/OdooServicesIcons/manufacturing.jpg';
import RetailImage from '../../../../Assets/Odoo/OdooServicesIcons/retail.jpg';
import WholesaleImage from '../../../../Assets/Odoo/OdooServicesIcons/wholesale.jpg';
import ServicesImage from '../../../../Assets/Odoo/OdooServicesIcons/service.jpg';
import EducationImage from '../../../../Assets/Odoo/OdooServicesIcons/education.jpg';
import HealthcareImage from '../../../../Assets/Odoo/OdooServicesIcons/healthcare.jpg';
import ERPImage from '../../../../Assets/Odoo/OdooServicesIcons/erp.jpg';

const industries = [
  {
    title: 'Manufacturing',
    headline: 'Keep production moving.',
    description: 'Connect production, inventory, and supply chains in Odoo. Give your teams the visibility to reduce bottlenecks and manage costs.',
    points: ['Production planning', 'Supply chain visibility', 'Inventory & cost control'],
    image: ManufacturingImage,
  },
  {
    title: 'Retail & eCommerce',
    headline: 'One view of every sales channel.',
    description: 'Bring your stores and online business together with connected sales, stock, orders, and customer information.',
    points: ['POS & online storefronts', 'Order & inventory management', 'Customer insights'],
    image: RetailImage,
  },
  {
    title: 'Wholesale & Distribution',
    headline: 'From procurement to delivery.',
    description: 'Coordinate purchasing, warehouses, and fulfillment with real-time stock visibility across locations.',
    points: ['Procurement & replenishment', 'Multi-location inventory', 'Warehouse & order fulfillment'],
    image: WholesaleImage,
  },
  {
    title: 'Professional Services',
    headline: 'More clarity on every project.',
    description: 'Connect your people, projects, and billing to track delivery, manage client relationships, and improve productivity.',
    points: ['Project & task planning', 'Timesheets & billing', 'Contracts & client communication'],
    image: ServicesImage,
  },
  {
    title: 'Healthcare',
    headline: 'Simplify day-to-day operations.',
    description: 'Bring appointments, pharmacy inventory, and invoicing into connected workflows that reduce administrative effort.',
    points: ['Appointment management', 'Pharmacy inventory', 'Invoicing & operational reporting'],
    image: HealthcareImage,
  },
  {
    title: 'Education',
    headline: 'Less administration. More learning.',
    description: 'Centralize admissions, academic planning, and fees so your teams can coordinate resources and support students.',
    points: ['Admissions & scheduling', 'Fee & course management', 'Student communication'],
    image: EducationImage,
  },
  {
    title: 'Our Approach',
    headline: 'Odoo shaped around your business.',
    description: 'We align Odoo with your workflows and industry goals to reduce manual work and give your teams clearer operational insight.',
    points: ['Industry-specific workflows', 'Collaborative implementation', 'Scalable ERP configuration'],
    image: ERPImage,
  },
];

export default function OdooIndustries() {
  const [activeIndex, setActiveIndex] = useState(0);
  const activeIndustry = industries[activeIndex];

  return (
    <section
      className="px-4 py-14 sm:px-6 sm:py-16 lg:px-8"
      style={{ backgroundColor: '#FFFFFF' }}
      aria-labelledby="odoo-industries-heading"
    >
      <div className="mx-auto max-w-7xl">
        <div className="mb-8 flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-center lg:gap-12">
          <div>
            <h2 id="odoo-industries-heading" className="m-0 text-3xl font-bold text-center leading-tight tracking-tight sm:text-4xl" style={{ color: '#111111' }}>
              Your industry. Your Odoo.
            </h2>
          </div>
        </div>

        <div className="mb-6 flex flex-wrap gap-2" role="group" aria-label="Choose an industry">
          {industries.map((industry, index) => {
            const selected = index === activeIndex;
            return (
              <button
                key={industry.title}
                id={`odoo-industry-button-${index}`}
                type="button"
                onClick={() => setActiveIndex(index)}
                aria-pressed={selected}
                aria-controls="odoo-industry-details"
                className="rounded-full border px-4 py-2.5 text-xs font-semibold focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 sm:text-sm motion-safe:transition-transform motion-safe:hover:-translate-y-0.5"
                style={{
                  backgroundColor: selected ? '#714B67' : '#FFFFFF',
                  color: selected ? '#FFFFFF' : '#34303A',
                  borderColor: selected ? '#714B67' : '#E2DCE1',
                  outlineColor: '#714B67',
                }}
              >
                {industry.title}
              </button>
            );
          })}
        </div>

        <div
          id="odoo-industry-details"
          role="region"
          aria-labelledby={`odoo-industry-button-${activeIndex}`}
          className="grid overflow-hidden rounded-3xl border lg:grid-cols-2"
          style={{ backgroundColor: '#FAF7F9', borderColor: '#E9E1E7' }}
        >
          <div className="flex flex-col justify-center p-6 sm:p-8 lg:p-10">
            <div className="mb-5 flex items-center gap-2 text-md font-bold uppercase tracking-widest" style={{ color: '#714B67' }}>
              <span>{activeIndustry.title}</span>
              <FiArrowUpRight className="h-4 w-4" aria-hidden="true" />
            </div>
            <h3 className="m-0 text-2xl font-bold leading-tight tracking-tight sm:text-3xl" style={{ color: '#111111' }}>
              {activeIndustry.headline}
            </h3>
            <p className="mb-0 mt-4 text-left text-sm font-medium leading-relaxed sm:text-base" style={{ color: '#34303A', hyphens: 'none', wordBreak: 'normal' }}>
              {activeIndustry.description}
            </p>
            <ul className="mb-0 mt-6 list-none space-y-3 p-0">
              {activeIndustry.points.map((point) => (
                <li key={point} className="flex items-start gap-3 text-sm font-medium" style={{ color: '#26222A' }}>
                  <FiCheck className="mt-0.5 h-4 w-4 shrink-0" style={{ color: '#714B67' }} aria-hidden="true" />
                  <span>{point}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="flex items-center justify-center overflow-hidden p-4 sm:p-6 lg:p-8" style={{ backgroundColor: '#FFFFFF' }}>
            <img
              key={activeIndustry.title}
              src={activeIndustry.image}
              alt={activeIndustry.title === 'Our Approach' ? 'Industry-focused ERP solutions' : activeIndustry.title}
              className="h-56 w-full rounded-2xl object-contain sm:h-64 lg:h-80"
              decoding="async"
            />
          </div>
        </div>
      </div>
    </section>
  );
}
